import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const AuthForm = () => {
    const [mode, setMode] = useState('login'); // 'login', 'register', 'forgot'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [turnstileToken, setTurnstileToken] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const { localLogin, register, forgotPassword } = useAuth();
    const turnstileRef = useRef(null);

    // Turnstile widget ID
    const widgetIdRef = useRef(null);

    useEffect(() => {
        // Site key from env or testing key
        const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

        const renderTurnstile = () => {
            if (window.turnstile && turnstileRef.current && !widgetIdRef.current) {
                widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
                    sitekey: siteKey,
                    callback: (token) => {
                        setTurnstileToken(token);
                    },
                    'expired-callback': () => setTurnstileToken(''),
                    'error-callback': () => setTurnstileToken(''),
                    theme: 'dark'
                });
            }
        };

        if (mode === 'register' || mode === 'forgot') {
            // Give a tiny bit of time for the DOM element to be available
            const timer = setTimeout(renderTurnstile, 0);
            return () => clearTimeout(timer);
        } else {
            if (widgetIdRef.current) {
                try {
                    window.turnstile.remove(widgetIdRef.current);
                } catch (e) {}
                widgetIdRef.current = null;
                setTurnstileToken('');
            }
        }
    }, [mode]);

    const validatePassword = (pass) => {
        if (pass.length < 8) return "Password must be at least 8 characters";
        if (!/[A-Z]/.test(pass)) return "Password must contain an uppercase letter";
        if (!/[a-z]/.test(pass)) return "Password must contain a lowercase letter";
        if (!/[0-9!@#$%^&*(),.?":{}|<>]/.test(pass)) return "Password must contain a digit or special character";
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        if ((mode === 'register' || mode === 'forgot') && !turnstileToken) {
            setError("Please complete the bot challenge");
            return;
        }

        setLoading(true);

        if (mode === 'register') {
            const passError = validatePassword(password);
            if (passError) {
                setError(passError);
                setLoading(false);
                return;
            }
            if (password !== confirmPassword) {
                setError("Passwords do not match");
                setLoading(false);
                return;
            }
        }

        try {
            if (mode === 'login') {
                await localLogin(email, password);
                // Success will trigger checkAuth which handles redirection via PublicLayout
            } else if (mode === 'register') {
                const res = await register(email, password, turnstileToken);
                setMessage(res.message || 'Registration successful! Check your email to verify your account.');
                setMode('login'); // Switch to login after registration
            } else if (mode === 'forgot') {
                const res = await forgotPassword(email, turnstileToken);
                setMessage(res.message || 'If your email is registered, you will receive a reset link shortly.');
            }
        } catch (err) {
            setError(err.response?.data?.detail || err.message || 'An error occurred');
            // Reset Turnstile on error
            if (widgetIdRef.current) window.turnstile.reset(widgetIdRef.current);
            setTurnstileToken('');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full max-w-sm mx-auto">
            <h2 className="text-2xl font-bold text-white text-center mb-6">
                {mode === 'login' ? 'Welcome Back' : mode === 'register' ? 'Create Account' : 'Reset Password'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-text-muted mb-1">Email</label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full p-3 bg-bg-darkest border border-border-light rounded text-white focus:outline-none focus:border-accent-blue"
                        placeholder="your@email.com"
                    />
                </div>
                
                {mode !== 'forgot' && (
                    <>
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="block text-sm font-medium text-text-muted">Password</label>
                                {mode === 'login' && (
                                    <button 
                                        type="button"
                                        onClick={() => setMode('forgot')}
                                        className="text-[11px] text-accent-blue hover:underline"
                                    >
                                        Forgot Password?
                                    </button>
                                )}
                            </div>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="w-full p-3 bg-bg-darkest border border-border-light rounded text-white focus:outline-none focus:border-accent-blue"
                                placeholder="••••••••"
                            />
                            {mode === 'register' && (
                                <p className="text-[10px] text-gray-500 mt-1">
                                    Min 8 chars, Upper, Lower, Symbol/Digit
                                </p>
                            )}
                        </div>

                        {mode === 'register' && (
                            <div>
                                <label className="block text-sm font-medium text-text-muted mb-1">Confirm Password</label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    className="w-full p-3 bg-bg-darkest border border-border-light rounded text-white focus:outline-none focus:border-accent-blue"
                                    placeholder="••••••••"
                                />
                            </div>
                        )}
                    </>
                )}

                {(mode === 'register' || mode === 'forgot') && (
                    <div className="flex justify-center py-2">
                        <div ref={turnstileRef}></div>
                    </div>
                )}

                {error && (
                    <div className="p-3 bg-accent-red/20 border border-accent-red/50 rounded text-accent-red text-sm">
                        {error}
                    </div>
                )}

                {message && (
                    <div className="p-3 bg-green-900/20 border border-green-500/50 rounded text-green-400 text-sm">
                        {message}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-3 rounded font-bold text-white transition-all ${
                        loading ? 'bg-gray-600 cursor-not-allowed' : 'bg-accent-blue hover:bg-blue-600'
                    }`}
                >
                    {loading ? 'Processing...' : mode === 'login' ? 'Login' : mode === 'register' ? 'Register' : 'Send Reset Link'}
                </button>
            </form>

            <div className="mt-6 text-center space-y-2">
                {mode === 'login' ? (
                    <button
                        onClick={() => {
                            setMode('register');
                            setError('');
                            setMessage('');
                            setTurnstileToken('');
                        }}
                        className="text-sm text-accent-blue hover:underline block w-full"
                    >
                        Don't have an account? Register
                    </button>
                ) : (
                    <button
                        onClick={() => {
                            setMode('login');
                            setError('');
                            setMessage('');
                            setTurnstileToken('');
                        }}
                        className="text-sm text-accent-blue hover:underline block w-full"
                    >
                        Back to Login
                    </button>
                )}
            </div>
        </div>
    );
};

export default AuthForm;
